import { motion, AnimatePresence } from "framer-motion";
import { X, Minus, Plus, ArrowRight, Loader2, Gift } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useCart, SETUP_FEE_LABEL } from "@/context/CartContext";
import { track } from "@/lib/analytics";
import api, { money } from "@/lib/api";
import { ShippingStep } from "@/components/ShippingStep";

const CartDrawer = () => {
  const { items, open, setOpen, removeItem, setQuantity, total, setupFee } = useCart();
  const [loading, setLoading] = useState(false);
  const [quote, setQuote] = useState(null);
  const [quoteState, setQuoteState] = useState("idle");
  const [step, setStep] = useState("cart");
  useEffect(() => { if (!open || !items.length) setStep("cart"); }, [open, items.length]);
  const [giftCode, setGiftCode] = useState("");
  const [giftCard, setGiftCard] = useState(null);
  const [applying, setApplying] = useState(false);
  const [rush, setRush] = useState(false);

  const payload = () => items.map((i) => ({
    lookup_key: i.lookup_key, quantity: i.quantity, engraving: i.engraving || "", notes: i.notes || "",
    design_file_id: i.design_file_id || "", design_filename: i.design_filename || "", colour: !!i.colour, yeti: !!i.yeti,
    upgrade: !!i.upgrade, artwork: i.artwork || "new", artwork_reference: i.artwork_reference || "", coat: i.coat || "", font: i.font || "",
  }));

  useEffect(() => {
    if (!items.length) { setQuote(null); setQuoteState("idle"); return; }
    let live = true;
    setQuoteState("loading");
    const t = setTimeout(() => {
      api.post("/cart/quote", { items: payload(), rush })
        .then((r) => { if (live) { setQuote(r.data); setQuoteState("ready"); } })
        .catch(() => { if (live) { setQuote(null); setQuoteState("error"); } });
    }, 250);
    return () => { live = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, rush]);

  const volumeDiscount = quote?.volume_discount || 0;
  const rushAmount = quote ? quote.rush_amount : rush ? Math.round((total - volumeDiscount) * 0.25) : 0;
  const grand = quote ? quote.amount : total + rushAmount;
  const blockers = quote?.errors || (quoteState === "error" ? [{ code: "quote_failed", message: "We couldn't price this order automatically. Please adjust quantities or request a custom quote." }] : []);
  const checkoutBlocked = blockers.length > 0 || quoteState !== "ready";
  const giftDiscount = giftCard ? Math.min(giftCard.balance, Math.max(0, grand - 100)) : 0;

  const applyGift = async () => {
    const code = giftCode.trim().toUpperCase();
    if (!code) return;
    setApplying(true);
    try {
      const { data } = await api.get(`/giftcards/${code}`);
      if (data.status !== "active" || data.balance <= 0) {
        toast.error("This gift card has no remaining balance.");
      } else {
        setGiftCard(data);
        toast.success(`Gift card applied — ${money(data.balance)} available`);
      }
    } catch {
      toast.error("Gift card not found — check the code.");
    }
    setApplying(false);
  };

  const checkout = async (shipping) => {
    setLoading(true);
    try {
      const { data } = await api.post("/checkout", {
        items: items.map((i) => ({
          lookup_key: i.lookup_key,
          quantity: i.quantity,
          engraving: i.engraving,
          notes: i.notes,
          design_file_id: i.design_file_id || "",
          design_filename: i.design_filename || "",
          colour: !!i.colour,
          yeti: !!i.yeti,
          upgrade: !!i.upgrade,
          artwork: i.artwork || "new",
          artwork_reference: i.artwork_reference || "",
          coat: i.coat || "",
          font: i.font || "",
        })),
        origin_url: window.location.origin,
        gift_card_code: giftCard?.code || "",
        rush,
        shipping,
      });
      track("checkout_started", { items: items.length, quantity: items.reduce((n, i) => n + i.quantity, 0) });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60]"
            data-testid="cart-overlay"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", ease: [0.22, 1, 0.36, 1], duration: 0.5 }}
            className="fixed top-0 right-0 h-full w-full sm:w-[440px] bg-[#0C0C0C] border-l border-zinc-800 z-[70] flex flex-col"
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between px-6 py-6 border-b border-zinc-800">
              <h3 className="font-serif-display text-2xl text-white">Your Order</h3>
              <button data-testid="cart-close-btn" onClick={() => setOpen(false)} className="text-zinc-500 hover:text-[#C87455] transition-colors">
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
              {step === "shipping" ? (
                <>
                  <p className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#C87455]">Step 2 of 3 · Delivery</p>
                  <ShippingStep items={items} onBack={() => setStep("cart")} onContinue={checkout} loading={loading} onNavigate={() => setOpen(false)} />
                </>
              ) : items.length === 0 ? (
                <p data-testid="cart-empty-message" className="text-zinc-500 text-sm font-mono-tech uppercase tracking-widest text-center mt-20">
                  Nothing here yet
                </p>
              ) : (
                items.map((item) => (
                  <div key={item.key} data-testid={`cart-item-${item.slug}`} className="flex gap-4 border-b border-zinc-900 pb-6">
                    <div className="w-20 h-20 bg-[#121212] shrink-0 overflow-hidden">
                      <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between gap-2">
                        <p className="text-sm text-white">{item.name}</p>
                        <button
                          data-testid={`cart-remove-${item.slug}`}
                          onClick={() => removeItem(item.key)}
                          className="text-zinc-600 hover:text-red-400 transition-colors shrink-0"
                        >
                          <X size={14} />
                        </button>
                      </div>
                      {item.engraving && (
                        <p className="font-mono-tech text-[10px] text-[#C87455] mt-1 truncate">"{item.engraving}"</p>
                      )}
                      {item.colour && (
                        <p className="font-mono-tech text-[10px] text-[#C87455] mt-1">+ Oxidised colour</p>
                      )}
                      {item.yeti && (
                        <p className="font-mono-tech text-[10px] text-[#C87455] mt-1">+ {item.yeti_label || "Yeti upgrade"}</p>
                      )}
                      {item.upgrade && (
                        <p className="font-mono-tech text-[10px] text-[#C87455] mt-1">+ {item.upgrade_label || "Upgrade"}</p>
                      )}
                      <p className={`font-mono-tech text-[10px] mt-1 ${item.artwork === "reorder" ? "text-zinc-400" : "text-[#C87455]"}`} data-testid={`cart-artwork-${item.slug}`}>
                        {item.artwork === "reorder" ? `Reorder · ref ${item.artwork_reference || "—"} · no setup fee` : "New artwork · setup fee once per order"}
                      </p>
                      {item.pack_size > 0 && (
                        <p className="font-mono-tech text-[10px] text-zinc-500 mt-1" data-testid="cart-item-pack">{item.quantity} pack{item.quantity > 1 ? "s" : ""} · {item.pack_size * item.quantity} cards · pack pricing, no volume discount</p>
                      )}
                      {item.coat && (
                        <p className="font-mono-tech text-[10px] text-zinc-400 mt-1">Coat: {item.coat}</p>
                      )}
                      {item.font && (
                        <p className="font-mono-tech text-[10px] text-zinc-400 mt-1" data-testid="cart-item-font">Style: {item.font}</p>
                      )}
                      {item.design_filename && (
                        <p className="font-mono-tech text-[10px] text-zinc-500 mt-1 truncate">⎘ {item.design_filename}</p>
                      )}
                      <div className="flex items-center justify-between mt-3">
                        <div className="flex items-center border border-zinc-800">
                          <button
                            data-testid={`cart-qty-minus-${item.slug}`}
                            onClick={() => setQuantity(item.key, item.quantity - 1)}
                            className="px-2 py-1 text-zinc-400 hover:text-white"
                          >
                            <Minus size={12} />
                          </button>
                          <span className="px-3 text-xs font-mono-tech text-white">{item.quantity}</span>
                          <button
                            data-testid={`cart-qty-plus-${item.slug}`}
                            onClick={() => setQuantity(item.key, item.quantity + 1)}
                            className="px-2 py-1 text-zinc-400 hover:text-white"
                          >
                            <Plus size={12} />
                          </button>
                        </div>
                        <span className="font-mono-tech text-sm text-zinc-300">{money(item.amount * item.quantity)}</span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {items.length > 0 && step === "cart" && (
              <div className="border-t border-zinc-800 px-6 py-6 space-y-4">
                <button
                  type="button"
                  data-testid="rush-toggle"
                  onClick={() => setRush(!rush)}
                  className={`w-full text-left border transition-colors duration-300 px-4 py-3 flex items-center gap-3 ${rush ? "border-[#C87455] bg-[#C87455]/10" : "border-zinc-800 hover:border-zinc-600"}`}
                >
                  <span className={`h-3.5 w-3.5 shrink-0 border flex items-center justify-center ${rush ? "border-[#C87455] bg-[#C87455]" : "border-zinc-600"}`}>
                    {rush && <span className="text-black text-[9px] leading-none">✓</span>}
                  </span>
                  <span className="flex-1">
                    <span className={`font-mono-tech text-[10px] uppercase tracking-[0.2em] block ${rush ? "text-[#C87455]" : "text-zinc-300"}`}>Rush production — +25%</span>
                    <span className="text-zinc-500 text-[11px] block">Jump the queue · ships in 48 hours</span>
                  </span>
                  <span className={`font-mono-tech text-xs ${rush ? "text-[#C87455]" : "text-zinc-500"}`} data-testid="rush-amount">{rush ? `+${money(rushAmount)}` : `+${money(Math.round(total * 0.25))}`}</span>
                </button>
                {giftCard ? (
                  <div className="flex items-center justify-between border border-[#C87455]/40 bg-[#C87455]/5 px-4 py-3" data-testid="giftcard-applied">
                    <div className="flex items-center gap-2 font-mono-tech text-xs text-zinc-300">
                      <Gift size={13} className="text-[#C87455]" /> {giftCard.code} · −{money(giftDiscount)}
                    </div>
                    <button data-testid="giftcard-remove-btn" onClick={() => setGiftCard(null)} className="text-zinc-500 hover:text-red-400 transition-colors">
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input
                      data-testid="giftcard-code-input"
                      value={giftCode}
                      onChange={(e) => setGiftCode(e.target.value.toUpperCase())}
                      placeholder="Gift card code"
                      className="flex-1 bg-transparent border-b border-zinc-800 focus:border-[#C87455] transition-colors rounded-none px-0 py-2 text-white placeholder-zinc-600 font-mono-tech text-xs tracking-widest"
                    />
                    <button
                      data-testid="giftcard-apply-btn"
                      onClick={applyGift}
                      disabled={applying || !giftCode.trim()}
                      className="border border-zinc-700 text-zinc-300 hover:border-[#C87455]/60 hover:text-[#C87455] transition-colors px-4 py-2 font-mono-tech text-[10px] uppercase tracking-[0.2em] disabled:opacity-40"
                    >
                      {applying ? "…" : "Apply"}
                    </button>
                  </div>
                )}
                <div className="flex justify-between items-baseline" data-testid="cart-setup-fee-row">
                  <span className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-zinc-500">{SETUP_FEE_LABEL}</span>
                  <span className={`font-mono-tech text-sm ${(quote?.setup_fee ?? setupFee) ? "text-zinc-300" : "text-zinc-500"}`} data-testid="cart-setup-fee">
                    {(quote?.setup_fee ?? setupFee) ? money(quote?.setup_fee ?? setupFee) : "No fee · reorder"}
                  </span>
                </div>
                {quote && (
                  <div className="flex justify-between items-baseline" data-testid="cart-volume-row">
                    <span className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                      Volume pricing · {quote.eligible_units} eligible unit{quote.eligible_units === 1 ? "" : "s"}{quote.volume_percent ? ` · ${quote.volume_percent}% off` : ""}
                    </span>
                    <span className={`font-mono-tech text-sm ${volumeDiscount ? "text-[#C87455]" : "text-zinc-500"}`} data-testid="cart-volume-discount">
                      {volumeDiscount ? `−${money(volumeDiscount)}` : quote.eligible_units < 10 ? "5% off at 10+" : "—"}
                    </span>
                  </div>
                )}
                {blockers.map((b, idx) => (
                  <div key={`${b.code}-${idx}`} className="border border-amber-400/30 bg-amber-400/5 p-3" data-testid={`cart-blocker-${b.code}`}>
                    <p className="text-amber-200 text-xs leading-relaxed">{b.message}</p>
                    {b.code !== "reorder_reference" && <Link to="/quote?type=business" onClick={() => setOpen(false)} data-testid="cart-blocker-quote-link" className="inline-block mt-2 font-mono-tech text-[10px] uppercase tracking-[0.2em] text-[#C87455] link-line">Request a custom quote →</Link>}
                  </div>
                ))}
                <div className="flex justify-between items-baseline">
                  <span className="font-mono-tech text-xs uppercase tracking-[0.2em] text-zinc-500">Subtotal</span>
                  <span data-testid="cart-total" className="font-serif-display text-2xl text-white">
                    {giftDiscount > 0 ? (
                      <>
                        <span className="text-zinc-600 line-through text-lg mr-3">{money(grand)}</span>
                        {money(grand - giftDiscount)}
                      </>
                    ) : (
                      money(grand)
                    )}
                  </span>
                </div>
                <p className="font-mono-tech text-[10px] text-zinc-400 uppercase tracking-wider" data-testid="cart-tax-notice">CAD · GST extra · Shipping calculated to your address in the next step</p>
                <button
                  data-testid="checkout-btn"
                  onClick={() => setStep("shipping")}
                  disabled={loading || checkoutBlocked}
                  className="w-full bg-[#C87455] text-black hover:bg-[#A65B3E] transition-colors px-8 py-4 font-mono-tech uppercase tracking-[0.2em] text-sm flex items-center justify-center gap-3 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading || quoteState === "loading" ? <Loader2 size={16} className="animate-spin" /> : blockers.length ? (blockers.every((b) => b.code === "reorder_reference") ? "Reference required" : "Quote required") : <>Delivery &amp; payment <ArrowRight size={14} /></>}
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartDrawer;
